/**
 * @fileoverview React context for inline violation capture.
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - Expose the per-field violation read / write surface to widgets without
 *   prop-drilling through SectionRenderer → FieldRenderer → individual widget.
 * - Wire `<InspectionRenderer>`'s `persistAdapter` into one host-injectable
 *   context so `<FieldViolationCaptures>` (mounted automatically by
 *   `<FieldRenderer>`) can read existing violations and persist edits.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `LoggedViolation` (renderer-local).
 * - `ViolationMapping` (core-domain).
 *
 * Architecture:
 * - Pure React Context. State lives in the host's PersistAdapter — this
 *   context only exposes a stable adapter-like surface to widgets.
 *
 * @hook useViolationsCapture
 * Purpose: read the inline-capture surface for the active inspection.
 * Returns: ViolationsCaptureContextValue | null
 * Side Effects: none.
 *
 * @contract ViolationsCaptureContextValue
 * Purpose: minimum surface a host needs to wire in for inline violation
 * capture to render below failing fields.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { ViolationMapping } from '@firesquirrel/core-domain';
import type { LoggedViolation } from './types';

export interface ViolationsCaptureContextValue {
  /** Currently logged violations across all fields/forms. */
  violations: readonly LoggedViolation[];
  /** Inspector id, written onto new occurrences as `discoveredBy`. */
  inspectorId: string;
  /** 0-based event index, written onto new occurrences as `discoveredInEventIndex`. */
  eventIndex: number;
  /** When true, capture surfaces render but disable all edit affordances. */
  readOnly: boolean;
  /**
   * Persist a violation update. `next === null` removes the violation
   * entirely from the adapter. Lookup key is `(fieldId, mapping.code,
   * mapping.codeSetId)`.
   */
  setViolation(fieldId: string, mapping: ViolationMapping, next: LoggedViolation | null): void;
  /**
   * Find an existing violation for the given field + mapping. Returns null
   * when the inspector has not started capturing for this pairing yet.
   */
  getViolation(fieldId: string, mapping: ViolationMapping): LoggedViolation | null;
}

const ViolationsCaptureContext = createContext<ViolationsCaptureContextValue | null>(null);

export function useViolationsCapture(): ViolationsCaptureContextValue | null {
  return useContext(ViolationsCaptureContext);
}

export interface ViolationsCaptureProviderProps {
  value: ViolationsCaptureContextValue;
  children: ReactNode;
}

export function ViolationsCaptureProvider({ value, children }: ViolationsCaptureProviderProps): ReactNode {
  return (
    <ViolationsCaptureContext.Provider value={value}>
      {children}
    </ViolationsCaptureContext.Provider>
  );
}
